import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Box,
  Typography,
  Button,
  Divider,
  Alert,
  Paper,
  Chip
} from "@mui/material";
import {
  Bolt,
  Search,
  Speed,
  Security
} from "@mui/icons-material";

const PrintTypeSelector = ({
  open,
  pendingPrintType,
  setPendingPrintType,
  onSave,
}) => (
  <Dialog 
    open={open} 
    disableEscapeKeyDown 
    maxWidth="sm" 
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: 3,
        padding: "0 10px 0 10px",
        boxShadow: "0 20px 60px rgba(255, 152, 0, 0.15)"
      }
    }}
  >
    <DialogTitle
      sx={{ 
        fontWeight: 800, 
        color: "#ff9800", 
        textAlign: "center",
        fontSize: "1.5rem",
        pb: 1
      }}
    >
      Configuração de Impressão
    </DialogTitle>
    
    <DialogContent sx={{ p: 3 }}>
      <Typography 
        variant="body1" 
        sx={{ 
          textAlign: "center", 
          mb: 3, 
          color: "text.secondary",
          fontWeight: 500
        }}
      >
        Escolha o modo de impressão que melhor se adapta ao seu fluxo de trabalho
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Paper
          elevation={pendingPrintType === "simplificada" ? 8 : 2}
          sx={{
            p: 3,
            borderRadius: 3,
            border: pendingPrintType === "simplificada" 
              ? "2px solid #ff9800" 
              : "2px solid transparent",
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              elevation: 6,
              transform: "translateY(-2px)"
            }
          }}
          onClick={() => setPendingPrintType("simplificada")}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Checkbox
              checked={pendingPrintType === "simplificada"}
              onChange={() => setPendingPrintType("simplificada")}
              sx={{ 
                color: "#ff9800",
                mt: -0.5,
                "&.Mui-checked": {
                  color: "#ff9800"
                }
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Speed sx={{ color: "#ff9800", fontSize: "1.2rem" }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#ff9800",
                    fontSize: "1.1rem"
                  }}
                >
                  Impressão Rápida
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "text.secondary",
                  lineHeight: 1.6,
                  mb: 1
                }}
              >
                Ideal para operações ágeis. Após escanear o código da nota fiscal, 
                a etiqueta é impressa <strong>automaticamente</strong> sem validações adicionais.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip 
                  icon={<Bolt />}
                  label="Processo Rápido" 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
                <Chip 
                  label="Sem Validação EAN" 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={pendingPrintType === "detalhada" ? 8 : 2}
          sx={{
            p: 3,
            borderRadius: 3,
            border: pendingPrintType === "detalhada" 
              ? "2px solid #ff9800" 
              : "2px solid transparent",
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              elevation: 6,
              transform: "translateY(-2px)"
            }
          }}
          onClick={() => setPendingPrintType("detalhada")}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Checkbox
              checked={pendingPrintType === "detalhada"}
              onChange={() => setPendingPrintType("detalhada")}
              sx={{ 
                color: "#ff9800",
                mt: -0.5,
                "&.Mui-checked": {
                  color: "#ff9800"
                }
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Security sx={{ color: "#ff9800", fontSize: "1.2rem" }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#ff9800",
                    fontSize: "1.1rem"
                  }}
                >
                  Impressão Segura
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "text.secondary",
                  lineHeight: 1.6,
                  mb: 1
                }}
              >
                Máxima segurança com validação completa. Após escanear a nota fiscal, 
                você deve <strong>validar cada produto</strong> individualmente antes da impressão.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip 
                  icon={<Search />}
                  label="Validação EAN" 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
                <Chip 
                  label="Controle Total" 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </DialogContent>
    <DialogActions sx={{ justifyContent: "center", p: 3, pt: 2 }}>
      <Button
        onClick={onSave}
        variant="contained"
        color="warning"
        size="large"
        sx={{
          fontWeight: 700,
          borderRadius: 3,
          px: 6,
          py: 1.5,
          fontSize: "1.1rem",
          boxShadow: "0 8px 25px rgba(255, 152, 0, 0.3)",
          "&:hover": {
            boxShadow: "0 12px 35px rgba(255, 152, 0, 0.4)",
            transform: "translateY(-2px)"
          },
          "&:disabled": {
            bgcolor: "grey.300",
            color: "grey.500"
          }
        }}
        disabled={!pendingPrintType}
      >
        {pendingPrintType ? `Confirmar ${pendingPrintType === "simplificada" ? "Rápida" : "Segura"}` : "Selecione uma opção"}
      </Button>
    </DialogActions>
  </Dialog>
);

export default PrintTypeSelector;
